import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { showAlert } from '../../../store/alertSlice';
import { hasModule } from '../../../utils/capabilitiesHelper';
import {
  addContactRequest,
  clearAddContactSucceeded,
  createSegmentRequest,
  deleteContactRequest,
  deleteSegmentRequest,
  fetchContactStatsRequest,
  fetchSegmentsRequest,
  importContactsRequest,
  clearImportContactsSucceeded,
  updateContactRequest,
} from '../../../store/slices/contactsSlice';
import {
  CONTACT_STATS_API_ENABLED,
  DEFAULT_FILTER_ROW,
} from '../data/contactsData';
import { useContactsPagination } from './useContactsPagination';
import {
  buildAddContactPayload,
  buildCreateSegmentPayload,
  buildListContactsFilterPayload,
  buildUpdateContactPayload,
} from '../utils/contactsApiMapper';
import {
  deriveContactFilterOptions,
  filterContacts,
  getDefaultOperatorForField,
  hasValidFilters,
  mergeContactFilterOptions,
  optionsFromFilterValues,
} from '../utils/contactsUtils';
import {
  hasContactValidationErrors,
  validateAddContactForm,
  validateSegmentSave,
} from '../utils/contactsValidation';

const EMPTY_ADD_FORM = {
  name: '',
  phone: '',
  email: '',
  leadStatus: '',
  tags: '',
};

export function useContactsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const hasWhatsApp = useSelector((state) =>
    hasModule(state.capabilities?.capabilities, 'WHATSAPP')
  );
  const { contacts, loading, loaded, error, stats, statsLoading, statsError, segments, segmentsLoading, segmentsError, segmentsSaving, addContactSaving, addContactSucceeded, deleteContactSaving, deleteSegmentSaving, importContactsSaving, importContactsSucceeded } =
    useSelector((state) => state.contacts);

  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState([{ ...DEFAULT_FILTER_ROW }]);
  const [showSaveSegment, setShowSaveSegment] = useState(false);
  const [segmentName, setSegmentName] = useState('');
  const [segmentNameError, setSegmentNameError] = useState('');

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAttributeLibraryOpen, setIsAttributeLibraryOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
  const [addFormErrors, setAddFormErrors] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);
  const [filterOptionPool, setFilterOptionPool] = useState({
    leadStatuses: [],
    tags: [],
  });

  const listFilterParams = useMemo(
    () => buildListContactsFilterPayload(filters),
    [filters]
  );

  const {
    currentPage,
    totalPages,
    availablePages,
    hasNextPage,
    showPagination,
    handlePageChange,
    handlePrev,
    handleNext,
    retryFetch,
  } = useContactsPagination({ appliedSearch, filterParams: listFilterParams, hasWhatsApp });

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'segments' || tabParam === 'all') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!CONTACT_STATS_API_ENABLED || !hasWhatsApp) return;
    dispatch(fetchContactStatsRequest());
  }, [dispatch, hasWhatsApp]);

  useEffect(() => {
    if (!hasWhatsApp || activeTab !== 'segments') return;
    dispatch(fetchSegmentsRequest());
  }, [dispatch, hasWhatsApp, activeTab]);

  useEffect(() => {
    if (!addContactSucceeded) return;
    setSearchQuery('');
    setAppliedSearch('');
    setAddForm(EMPTY_ADD_FORM);
    setAddFormErrors({});
    setIsAddPanelOpen(false);
    dispatch(clearAddContactSucceeded());
  }, [addContactSucceeded, dispatch]);

  useEffect(() => {
    if (!importContactsSucceeded) return;
    setIsImportModalOpen(false);
    dispatch(clearImportContactsSucceeded());
  }, [importContactsSucceeded, dispatch]);

  const handleSearchSubmit = () => {
    setAppliedSearch(searchQuery.trim());
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setAppliedSearch('');
  };

  const filteredContacts = useMemo(
    () => filterContacts(contacts, { searchQuery: '', filters }),
    [contacts, filters]
  );

  useEffect(() => {
    if (!loaded || loading) return;
    const pageOptions = deriveContactFilterOptions(contacts);
    setFilterOptionPool((prev) => mergeContactFilterOptions(prev, pageOptions));
  }, [contacts, loaded, loading]);

  const filterOptions = useMemo(
    () =>
      mergeContactFilterOptions(
        filterOptionPool,
        optionsFromFilterValues(filters)
      ),
    [filterOptionPool, filters]
  );

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  const addFilterRow = () => {
    if (filters.length < 5) {
      setFilters([...filters, { ...DEFAULT_FILTER_ROW }]);
    }
  };

  const removeFilterRow = (index) => {
    if (filters.length > 1) {
      setFilters(filters.filter((_, rowIndex) => rowIndex !== index));
      return;
    }
    setFilters([{ ...DEFAULT_FILTER_ROW }]);
  };

  const updateFilter = (index, key, value) => {
    const nextFilters = [...filters];
    nextFilters[index] = { ...nextFilters[index], [key]: value };

    if (key === 'field') {
      nextFilters[index].operator = getDefaultOperatorForField(value);
      nextFilters[index].value = '';
      nextFilters[index].attributeKey = '';
    }

    setFilters(nextFilters);
  };

  const clearAllFilters = () => {
    setFilters([{ ...DEFAULT_FILTER_ROW }]);
    setShowSaveSegment(false);
    setSegmentName('');
    setSegmentNameError('');
  };

  const handleShowSaveSegment = () => {
    setSegmentNameError('');
    setShowSaveSegment(true);
  };

  const handleSegmentNameChange = (value) => {
    setSegmentName(value);
    if (segmentNameError) {
      setSegmentNameError('');
    }
  };

  const handleCancelSaveSegment = () => {
    setShowSaveSegment(false);
    setSegmentName('');
    setSegmentNameError('');
  };

  const handleSaveSegment = () => {
    const validationErrors = validateSegmentSave({ name: segmentName, uiFilters: filters });
    if (validationErrors.name) {
      setSegmentNameError(validationErrors.name);
    }
    if (validationErrors.name || validationErrors.filters) {
      dispatch(
        showAlert({
          message: validationErrors.name || validationErrors.filters,
          type: 'error',
        })
      );
      return;
    }

    dispatch(
      createSegmentRequest(
        buildCreateSegmentPayload({ name: segmentName, uiFilters: filters })
      )
    );
    setShowSaveSegment(false);
    setSegmentName('');
    setSegmentNameError('');
  };

  const handleImportSubmit = ({ file, mapping }) => {
    dispatch(importContactsRequest({ file, mapping }));
  };

  const handleAddFormChange = (field, value) => {
    setAddForm((current) => ({ ...current, [field]: value }));
    if (addFormErrors[field]) {
      setAddFormErrors((current) => ({ ...current, [field]: '' }));
    }
  };

  const handleOpenAddPanel = (open) => {
    if (!open && addContactSaving) return;
    if (!open) {
      setAddForm(EMPTY_ADD_FORM);
      setAddFormErrors({});
    }
    setIsAddPanelOpen(open);
  };

  const handleSaveContact = () => {
    const errors = validateAddContactForm(addForm);
    if (hasContactValidationErrors(errors)) {
      setAddFormErrors(errors);
      dispatch(showAlert({ message: 'Please fix the highlighted fields', type: 'error' }));
      return;
    }

    dispatch(addContactRequest(buildAddContactPayload(addForm)));
  };

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setIsProfileOpen(true);
  };

  const handleDeleteContact = (contact) => {
    setPendingDelete({ type: 'contact', item: contact });
  };

  const handleDeleteSegment = (segment) => {
    setPendingDelete({ type: 'segment', item: segment });
  };

  const handleCancelDelete = () => {
    if (deleteContactSaving || deleteSegmentSaving) return;
    setPendingDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete || deleteContactSaving || deleteSegmentSaving) return;

    const target = pendingDelete;
    setPendingDelete(null);

    if (target.type === 'contact') {
      dispatch(deleteContactRequest(target.item.id));
      if (selectedContact?.id === target.item.id) {
        setIsProfileOpen(false);
        setSelectedContact(null);
      }
      return;
    }

    dispatch(deleteSegmentRequest(target.item.id));
  };

  const handleUpdateContact = (formValues) => {
    if (!selectedContact) return;

    dispatch(
      updateContactRequest(
        buildUpdateContactPayload({
          contact: selectedContact,
          email: formValues.email,
          leadStatus: formValues.leadStatus,
          tags: formValues.tags,
        })
      )
    );

    const nextContact = {
      ...selectedContact,
      email: formValues.email?.trim() || null,
      leadStatus: formValues.leadStatus,
      tags: Array.isArray(formValues.tags)
        ? formValues.tags
        : String(formValues.tags || '')
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean),
    };
    setSelectedContact(nextContact);
  };

  const handleViewSegmentContacts = (segment) => {
    setFilters(segment.filters.map((filter) => ({ ...filter })));
    handleTabChange('all');
  };

  const handleUseInCampaign = () => {
    navigate('/whatsapp/campaigns/new');
  };

  const handleCreateSegmentHint = () => {
    handleTabChange('all');
    dispatch(
      showAlert({
        message: 'Apply Lead Status or Tags filters on All Contacts, then click Save as Segment.',
        type: 'success',
      })
    );
  };

  const retrySegments = () => {
    dispatch(fetchSegmentsRequest());
  };

  const retryContactStats = () => {
    if (!CONTACT_STATS_API_ENABLED) return;
    dispatch(fetchContactStatsRequest());
  };

  return {
    contacts,
    loading,
    loaded,
    error,
    stats,
    statsLoading,
    statsError,
    hasWhatsApp,
    currentPage,
    totalPages,
    availablePages,
    hasNextPage,
    showPagination,
    handlePageChange,
    handlePrev,
    handleNext,
    segments,
    segmentsLoading,
    segmentsError,
    segmentsSaving,
    enrichedSegments: segments,
    searchQuery,
    activeTab,
    filters,
    showSaveSegment,
    segmentName,
    segmentNameError,
    isImportModalOpen,
    importContactsSaving,
    isAddPanelOpen,
    isProfileOpen,
    isAttributeLibraryOpen,
    selectedContact,
    addForm,
    addFormErrors,
    addContactSaving,
    filteredContacts,
    filterOptions,
    setSearchQuery,
    handleSearchSubmit,
    handleSearchClear,
    appliedSearch,
    setIsImportModalOpen,
    setIsAddPanelOpen,
    setIsProfileOpen,
    setIsAttributeLibraryOpen,
    handleTabChange,
    addFilterRow,
    removeFilterRow,
    updateFilter,
    clearAllFilters,
    handleShowSaveSegment,
    handleSegmentNameChange,
    handleCancelSaveSegment,
    handleSaveSegment,
    handleImportSubmit,
    handleAddFormChange,
    handleOpenAddPanel,
    handleSaveContact,
    handleViewContact,
    handleDeleteContact,
    handleUpdateContact,
    handleViewSegmentContacts,
    handleDeleteSegment,
    handleUseInCampaign,
    handleCreateSegmentHint,
    pendingDelete,
    deleteConfirmLoading: deleteContactSaving || deleteSegmentSaving,
    handleCancelDelete,
    handleConfirmDelete,
    retryFetch,
    retrySegments,
    retryContactStats,
  };
}
